import React, { useEffect, useRef, useState } from "react";
import ModeSwitcher from "../../components/customer/ModeSwitcher";
import { useMenu } from "../../components/customer/MenuContext";
import { useNavigate } from "react-router-dom";
import { Flame, Sparkles } from "lucide-react";

// Strict type interfaces for robust type safety
interface ISubChildCategory {
  id: number;
  name: string;
  slug: string;
  parentName?: string
}

interface IChildCategory {
  id: number;
  name: string;
  slug?: string;
  children?: ISubChildCategory[];
}

interface IMenuItem {
  id: number;
  name: string;
  slug?: string;
  children?: IChildCategory[];
}

interface CategoryNavProps {
  currentMode: "Products" | "Pets" | "Services" | string;
  setCurrentMode: (mode: "Products" | "Pets" | "Services") => void;
  onLoginRequired: () => void;
}

interface IMappedSubCategory {
  title: string;
  items: ISubChildCategory[];
}

const CategoryNav: React.FC<CategoryNavProps> = ({
  currentMode,
  setCurrentMode,
}) => {
  const { menu } = useMenu() as { menu: IMenuItem[] };
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [localMenuCache, setLocalMenuCache] = useState<IMenuItem[]>([]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 1. Synchronize data context directly to localStorage once the API returns values
  useEffect(() => {
    if (menu && menu.length > 0) {
      localStorage.setItem("cached_menu_categories", JSON.stringify(menu));
      setLocalMenuCache(menu);
    }
  }, [menu]);

  // 2. Fallback to localStorage cache instantly if global API context is pending
  useEffect(() => {
    const savedMenu = localStorage.getItem("cached_menu_categories");
    if (savedMenu && (!menu || menu.length === 0)) {
      try {
        const parsedMenu = JSON.parse(savedMenu) as IMenuItem[];
        setLocalMenuCache(parsedMenu);
      } catch (e) {
        console.error("Failed parsing menu localStorage cache items:", e);
      }
    }
  }, [menu]);

  // Select current source mapping prioritizing global menu state
  const currentMenuSource: IMenuItem[] =
    menu && menu.length > 0 ? menu : localMenuCache;

  const rootMenu = currentMenuSource?.find(
    (item: IMenuItem) => item.name.toLowerCase() === currentMode.toLowerCase()
  );

  const allcategories: IChildCategory[] = rootMenu?.children || [];

  useEffect(() => {
    if (allcategories && allcategories.length > 0) {
      setActiveCategory(allcategories[0].name);
    } else {
      setActiveCategory("");
    }
  }, [currentMode, menu, rootMenu, allcategories.length]);

  const activeData = allcategories.find(
    (cat: IChildCategory) => cat.name === activeCategory
  );

  const subCategories: IMappedSubCategory[] =
    activeData?.children?.map((child: IChildCategory) => ({
      title: child.name,
      items:
        child.children?.map((subChild: ISubChildCategory) => ({
          id: subChild.id,
          name: subChild.name,
          slug: subChild.slug,
          parentName: subChild.parentName,
        })) || [],
    })) || [];

  const isSmallMenu: boolean = subCategories.length <= 5;

  const handleEnter = (): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (allcategories.length > 0) {
      setIsMenuOpen(true);
    }
  };

  const handleLeave = (): void => {
    timeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, 250);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* DESKTOP NAV BAR */}
      <nav className="hidden lg:flex bg-[#3A6A39] h-[40px] items-center px-4 justify-between">
        <div className="flex items-center gap-8 overflow-x-auto scrollbar-hide w-full">
          {allcategories.length === 0 ? (
            <div className="text-white/40 text-xs font-medium px-2 select-none">
              Loading categories...
            </div>
          ) : (
            <>
              {allcategories.map((cat: IChildCategory) => (
                <div
                  key={cat.id}
                  onMouseEnter={() => {
                    setActiveCategory(cat.name);
                    setIsMenuOpen(true);
                  }}
                >
                  <button
                    type="button"
                    className={`
                      px-2
                      h-[40px]
                      flex
                      items-center
                      font-semibold
                      transition-all
                      duration-200
                      text-sm
                      whitespace-nowrap
                      border-b-2
                      ${activeCategory === cat.name
                        ? "border-white text-white"
                        : "border-transparent text-white hover:text-gray-200"
                      }
                    `}
                  >
                    {cat.name}
                  </button>
                </div>
              ))}

              {/* Brands Category - Displayed in Products Mode Only */}
              {currentMode === "Products" && (
                <div onMouseEnter={() => setIsMenuOpen(false)}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate("/productpage/customerbrandpage");
                    }}
                    className="
                      px-2
                      h-[40px]
                      flex
                      items-center
                      font-semibold
                      transition-all
                      duration-200
                      text-sm
                      whitespace-nowrap
                      border-b-2
                      border-transparent
                      text-white
                      hover:text-gray-200
                      hover:border-white
                    "
                  >
                    Brands
                  </button>
                </div>
              )}

              {currentMode === "Products" && (
                <div onMouseEnter={() => setIsMenuOpen(false)}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);

                      navigate("/productpage/productListingScreen", {
                        state: {
                          source: "NewArrivals",
                          data: {
                            onlyNewArrivals: true,
                            title: "New Arrivals",
                          },
                        },
                      });
                    }}
                    className="
                        group
                        px-2
                        h-[40px]
                        flex
                        items-center
                        gap-1.5
                        font-semibold
                        transition-all
                        duration-200
                        text-sm
                        whitespace-nowrap
                        border-b-2
                        border-transparent
                        text-[#F7D547]
                        hover:text-[#FFE36A]
                        hover:border-[#F7D547]
                      "
                  >
                    <Flame
                      size={16}
                      strokeWidth={2.5}
                      className="transition-transform duration-200 group-hover:scale-110"
                    />
                    <span>New Arrivals</span>
                  </button>
                </div>
              )}

              {currentMode === "Products" && (
                <div onMouseEnter={() => setIsMenuOpen(false)}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);

                      navigate("/productpage/productListingScreen", {
                        state: {
                          source: "BestSeller",
                          data: {
                            onlyBestSeller: true,
                            title: "Best Sellers",
                          },
                        },
                      });
                    }}
                    className="
                        group
                        px-2
                        h-[40px]
                        flex
                        items-center
                        gap-1.5
                        font-semibold
                        transition-all
                        duration-200
                        text-sm
                        whitespace-nowrap
                        border-b-2
                        border-transparent
                        text-[#F7D547]
                        hover:text-[#FFE36A]
                        hover:border-[#F7D547]
                      "
                  >
                    <Sparkles
                      size={16}
                      strokeWidth={2.5}
                      className="transition-transform duration-200 group-hover:rotate-12"
                    />
                    <span>Best Seller</span>
                  </button>
                </div>
              )}

              {currentMode === "Products" && (
                <div onMouseEnter={() => setIsMenuOpen(false)}>
                  <button
                    type="button"
                    onClick={() => {
                      setIsMenuOpen(false);

                      navigate("/productpage/productListingScreen", {
                        state: {
                          source: "SpecialOffer",
                          data: {
                            onlyOnSale: true,
                            title: "Special Offers",
                          },
                        },
                      });
                    }}
                    className="
                        group
                        px-2
                        h-[40px]
                        flex
                        items-center
                        gap-1.5
                        font-semibold
                        transition-all
                        duration-200
                        text-sm
                        whitespace-nowrap
                        border-b-2
                        border-transparent
                        text-[#F7D547]
                        hover:text-[#FFE36A]
                        hover:border-[#F7D547]
                      "
                  >
                    <Sparkles
                      size={16}
                      strokeWidth={2.5}
                      className="transition-transform duration-200 group-hover:rotate-12"
                    />
                    <span>Special Offer</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </nav>

      {/* MOBILE NAV MODE SWITCHER CONTAINER */}
      <div className="md:hidden block">
        <ModeSwitcher
          activeMode={currentMode}
          onModeChange={setCurrentMode}
          innerclassName="w-full justify-center"
          classname="rounded-none w-full border-t border-t-gray-100 py-2"
        />
      </div>

      {/* EXPANDABLE MEGA MENU LAYOUT RAILS - HIDDEN ON MOBILE VIEW (hidden lg:block added) */}
      {isMenuOpen && allcategories.length > 0 && subCategories.length > 0 && (
        <div
          className={`
            hidden
            lg:block
            absolute
            top-full
            left-0
            w-full
            bg-white
            border
            border-gray-100
            shadow-xl
            z-[999]
            py-2
            ${isSmallMenu ? "px-8" : "px-6"}
          `}
        >
          <div
            className="
              grid
              gap-x-10
              gap-y-3
              grid-flow-col
              auto-cols-fr
            "
          >
            {subCategories.map((section: IMappedSubCategory, index: number) => (
              <div key={index} className="min-w-0">
                <h3 className="font-semibold text-logoHeading mb-2 text-sm uppercase border-b pb-1">
                  {section.title}
                </h3>

                <ul className="space-y-1">
                  {section.items.map((item: ISubChildCategory) => (
                    <li
                      key={item.id}
                      className="
                      text-sm
                      text-gray-700
                      leading-5
                      py-0.5
                      hover:text-mainBrandColor
                      cursor-pointer
                      transition-all
                      duration-200
                      hover:translate-x-1
                    "
                      onClick={() => {
                        const state = {
                          source: "Category",
                          data: {
                            categoryId: item.id,
                            title: item.name,
                            slug: item.slug,
                            parentName: item.parentName,
                          },
                        };

                        if (currentMode === "Pets") {
                          navigate("/productpage/customerpetlistingscreen", {
                            state,
                          });
                        }
                        else if (currentMode === 'Products') {
                          navigate("/productpage/productListingScreen", {
                            state,
                          });
                        }

                        else {
                          const serviceName = state.data?.parentName?.toLowerCase();
                          console.log(state, "ServiceName parent")
                          switch (serviceName) {
                            case "grooming":
                              navigate("/customergroomingbookingservice", {
                                state: { id: 26 },
                              });
                              break;

                            case "mobile grooming":
                              navigate("/mobilegroomingservice", {
                                state: { id: item.id, service: item },
                              });
                              break;

                            case "training":
                              navigate("/customergroomingbookingservice", {
                                state: { id: 28},
                              });
                              break;

                            case "pet taxi":
                              navigate("/pettaxiservice", {
                                state: { id:27 },
                              });
                              break;
                            case "Relocation":
                                    navigate("/pettaxiservice", {
                                state: { id:27 },
                              });
                              break;

                            default:
                              navigate("/customergroomingbookingservice", {
                                state: { id:26 },
                              });
                              break;
                          }
                        }

                        setIsMenuOpen(false);
                      }}
                    >
                      {item.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryNav;
